import { Box, IconButton, InputBase, MenuItem, makeStyles } from "@material-ui/core";
import Select from "legacy-components/form/container/Select";
import React, { KeyboardEvent, useRef } from "react";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";

const useStyles = makeStyles((theme) => ({
  filterIcon: {
    color: "#00548a"
  },
  sortIcon: {
    color: "#00548a",
    paddingTop: "4px"
  },
  iconButton: {
    backgroundColor: "transparent",
    padding: 0,
    marginLeft: "20px",
    '&:hover': {
      backgroundColor: "transparent"
    }
  },
  sortSelect: {
    minWidth: "150px",
    height: "32px"
  }
}));

export interface SortOption {
  value: string;
  label: string;
}

interface GenericFilterProps {
  onSearch: (searchText?: string) => void;
  onSortChange: (sortBy: string) => void;
  onSortDirectionChange: (direction: "asc" | "desc") => void;
  sortOptions: SortOption[];
  placeholder?: string;
  ariaLabel?: string;
  className?: string;
  sortBy: string;
  sortDirection: "asc" | "desc";
}

export const GenericFilter: React.FC<GenericFilterProps> = ({ 
  onSearch, 
  onSortChange,
  onSortDirectionChange,
  sortOptions,
  placeholder = "Search", 
  ariaLabel = "Search",
  className,
  sortBy,
  sortDirection,
}) => {
  const classes = useStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [selected, setSelected] = React.useState<boolean>(true);
  let previousValue = "";

  const onSearchClicked = () => {
    onSearch(inputRef?.current?.value);
    previousValue = inputRef?.current?.value ?? "";
    setSelected(false);
  };

  const onClearClicked = () => {
    onSearch("");
    if(inputRef.current) {
      inputRef.current.value = "";
    }
    setSelected(true);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      onSearchClicked();
      return;
    }
    if (previousValue !== inputRef.current?.value) {
      setSelected(true);
    }
  };

  const handleSortChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    onSortChange(event.target.value as string);
  };

  const toggleSortDirection = () => {
    onSortDirectionChange(sortDirection === "asc" ? "desc" : "asc");
  };

  return (
    <Box display="flex" mb="30px" paddingRight="10px">
      <Box display="flex" alignItems="center">
        <IconButton 
          className={classes.iconButton} 
          disableRipple
        >
          <i
            className={`modus-icons ${classes.filterIcon}`}
            aria-hidden="true"
          >
            filter
          </i>
        </IconButton>

        <Box
          className={className}
          style={{
            border: `1px solid #E0E1E9`,
            borderRadius: "2px",
            backgroundColor: "#fff",
            height: "32px",
            fontSize: "12px",
            display: "flex",
            width: "300px",
          }}
        >
          <InputBase
            style={{ marginLeft: "15px", flex: 1, height: "32px" }}
            placeholder={placeholder}
            inputProps={{ "aria-label": ariaLabel, fontSize: "12px" }}
            inputRef={inputRef}
            onKeyDown={handleKeyDown} 
          />
          {selected && (
            <IconButton
              type="button"
              style={{
                top: "-2px",
                padding: "10px",
                borderRadius: "2px",
                border: `1px solid #0063A3`,
                height: "32px",
                width: "32px",
              }}
              aria-label="search"
              onClick={onSearchClicked}
            >
              <SearchIcon color="primary" />
            </IconButton>
          )}
          {!selected && (
            <IconButton
              type="button"
              style={{
                top: "-2px",
                padding: "10px",
                height: "32px",
                width: "32px",
              }}
              aria-label="clear"
              onClick={onClearClicked}
            >
              <CloseIcon color="secondary" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box display="flex" justifyContent="space-evenly" marginLeft="30px">
        <Select
          id="applicationSort"
          className={`application-sort-select ${classes.sortSelect}`}
          IconComponent={ExpandMoreIcon}
          onChange={handleSortChange}
          value={sortBy}
        >
          {sortOptions.map((option) => (
            <MenuItem value={option.value} key={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>

        <IconButton 
          className={classes.iconButton} 
          onClick={toggleSortDirection}
          disableRipple
        >
          <i
            className={`modus-icons ${classes.sortIcon}`}
            aria-hidden="true"
          >
            {sortDirection === "asc" ? "sort_up" : "sort_down"}
          </i>
        </IconButton>
      </Box>
    </Box>
  );
};
